// src/utils/staffCopyConflicts.js

import { notifyStaffConflictInfo, requestStaffConflictPrompt } from './staffConflictPrompt';
import {
    normalizeStaffEntries,
    parseStaffEntry,
    formatStaffListForStore,
} from './staffEntry';

export { normalizeStaffEntries, parseStaffEntry } from './staffEntry';

const asName = (x) => parseStaffEntry(x)?.name || '';

const normName = (s) => String(s || '').trim();

function isSameTargetEvent(ev, targetRef) {
    if (!ev || !targetRef) return false;
    if (targetRef.gId && ev.gId) {
        return (
            ev.gId === targetRef.gId &&
            String(ev.calendarId || '') === String(targetRef.calendarId || '')
        );
    }
    return (
        normName(ev.event) === normName(targetRef.event || targetRef.name) &&
        String(ev.calendarId || '') === String(targetRef.calendarId || '')
    );
}

export function getConflictSets(eventsByDate, date, targetRef, unavailableByDate) {
    const assignedElsewhere = new Set();
    const unavailable = new Set();
    const list = Array.isArray(eventsByDate?.[date]) ? eventsByDate[date] : [];

    for (const ev of list) {
        if (isSameTargetEvent(ev, targetRef)) continue;
        for (const s of ev.staff || []) {
            const n = asName(s);
            if (n) assignedElsewhere.add(n);
        }
    }

    const unavail = Array.isArray(unavailableByDate?.[date]) ? unavailableByDate[date] : [];
    for (const u of unavail) {
        const n = asName(u);
        if (n) unavailable.add(n);
    }

    return { assignedElsewhere, unavailable };
}

/** Osoby niedostępne, które nadal widać na liście (nieprzypisane do wydarzenia). */
export function getVisibleUnavailableNames(unavailableNames, assignedNames) {
    const assigned = assignedNames instanceof Set ? assignedNames : new Set(assignedNames || []);
    const source = unavailableNames instanceof Set
        ? unavailableNames
        : new Set((Array.isArray(unavailableNames) ? unavailableNames : []).map(asName).filter(Boolean));
    const visible = new Set();
    for (const name of source) {
        const person = asName(name);
        if (person && !assigned.has(person)) visible.add(person);
    }
    return visible;
}

export function hasVisibleUnavailable(unavailableNames, assignedNames) {
    return getVisibleUnavailableNames(unavailableNames, assignedNames).size > 0;
}

/** @returns {'assigned'|'unavailable'|null} */
export function getStaffAssignmentConflict(name, { date, targetRef, eventsByDate, unavailableByDate }) {
    const person = asName(name);
    if (!person) return null;
    const { assignedElsewhere, unavailable } = getConflictSets(
        eventsByDate,
        date,
        targetRef,
        unavailableByDate
    );
    if (unavailable.has(person)) return 'unavailable';
    if (assignedElsewhere.has(person)) return 'assigned';
    return null;
}

export async function confirmStaffAssignmentConflict(name, reason, date) {
    const person = asName(name);
    if (reason === 'unavailable') {
        return requestStaffConflictPrompt({
            mode: 'confirm',
            title: 'Niedostępność',
            message: `${person} jest oznaczony/a jako niedostępny/a w dniu ${date}.`,
            items: [{ name: person, reason: 'unavailable' }],
            date,
        });
    }
    return requestStaffConflictPrompt({
        mode: 'confirm',
        title: 'Konflikt zajętości',
        message: `${person} jest już przypisany/a do innego wydarzenia w dniu ${date}.`,
        items: [{ name: person, reason: 'assigned' }],
        date,
    });
}

export async function confirmStaffAssignmentConflicts(conflicts, date) {
    if (!conflicts?.length) return true;
    return requestStaffConflictPrompt({
        mode: 'confirm',
        title: 'Konflikt zajętości',
        message: `W dniu ${date} wykryto konflikt zajętości:`,
        items: conflicts.map((c) => ({ name: c.name, reason: c.reason })),
        date,
    });
}

/**
 * Przy kopiowaniu: zachowuje tentative; konflikty wymagają potwierdzenia w popupie.
 */
export async function resolveStaffForCopy(staffList, { date, targetRef, eventsByDate, unavailableByDate }) {
    const entries = normalizeStaffEntries(staffList);
    const { assignedElsewhere, unavailable } = getConflictSets(
        eventsByDate,
        date,
        targetRef,
        unavailableByDate
    );

    const allowed = [];
    const conflicts = [];

    for (const entry of entries) {
        if (unavailable.has(entry.name)) {
            conflicts.push({ ...entry, reason: 'unavailable' });
        } else if (assignedElsewhere.has(entry.name)) {
            conflicts.push({ ...entry, reason: 'assigned' });
        } else {
            allowed.push(entry);
        }
    }

    if (conflicts.length === 0) {
        return { staff: formatStaffListForStore(allowed), hadDeclinedConflict: false };
    }

    const ok = await confirmStaffAssignmentConflicts(conflicts, date);
    if (!ok) {
        return { staff: formatStaffListForStore(allowed), hadDeclinedConflict: true };
    }

    return {
        staff: formatStaffListForStore([...allowed, ...conflicts]),
        hadDeclinedConflict: false,
    };
}

/** Przed ręcznym dodaniem osoby — zwraca false, gdy użytkownik anulował. */
export async function confirmStaffAssignmentIfNeeded(name, ctx) {
    const reason = getStaffAssignmentConflict(name, ctx);
    if (!reason) return true;
    return confirmStaffAssignmentConflict(name, reason, ctx.date);
}

export function notifyStaffCopyConflict() {
    notifyStaffConflictInfo('Część osób nie została skopiowana z powodu konfliktu zajętości.');
}

export function getBlockedNamesForStaffCopy(eventsByDate, date, targetRef, unavailableByDate) {
    const { assignedElsewhere, unavailable } = getConflictSets(
        eventsByDate,
        date,
        targetRef,
        unavailableByDate
    );
    return new Set([...assignedElsewhere, ...unavailable]);
}

export async function filterStaffForCopy(staffList, ctx) {
    const { staff, hadDeclinedConflict } = await resolveStaffForCopy(staffList, ctx);
    const input = normalizeStaffEntries(staffList);
    const hadConflict = hadDeclinedConflict || input.length > staff.length;
    return { staff, hadConflict };
}
